// apps/share-web/src/offline/kv-backend.ts
//
// Узкий контракт хранилища: четыре операции, ничего доменного.
//
// Зачем интерфейс, а не прямой вызов IndexedDB. В тестовом окружении этого
// приложения IndexedDB НЕТ: happy-dom его не реализует, а fake-indexeddb в
// дерево не поставлен (новая зависимость в общий lockfile ради одного файла).
// Прямой вызов означал бы, что ни одна строка логики кэша не покрыта. С
// внедряемым бэкендом покрыта вся логика, а непокрытым остаётся только
// адаптер из idb-backend.ts — четыре обёртки без единого решения.
// Долг записан: TD-260921-idb-adapter-untested.

// ОТКАЗЫ. Операции ЗАПИСИ (`set`, `del`) могут отказать, и это нормальный
// исход, а не баг: на iOS квота жёсткая, и `QuotaExceededError` приходит
// ровно тогда, когда телефон и так полон; при заблокированном политикой
// хранилище прилетит `SecurityError`. Потребитель обязан пережить отказ
// записи, НЕ роняя экран: офлайн — усиление, а не несущая часть, и человек,
// у которого не сохранилось, должен как минимум дочитать то, что открыто.
//
// ЧЕМ `memoryBackend` ОТЛИЧАЕТСЯ ОТ НАСТОЯЩЕГО. Он дубль, а не эмулятор, и
// расхождения названы здесь поимённо, чтобы на них не опирались тесты:
//   1. Хранит ССЫЛКУ, а не structured clone. Значение с функцией, инстансом
//      класса или Proxy внутри пройдёт здесь и бросит `DataCloneError` в
//      браузере. Кладите простые данные.
//   2. Мутация объекта ПОСЛЕ записи видна при следующем `get` — в IndexedDB
//      не видна: там лежит снимок.
//   3. Порядок ключей. У `Map` он порядок вставки, у `getAllKeys()` —
//      отсортированный. Тест, сверяющий точный МАССИВ ключей, держится за
//      артефакт дубля; сверять надо множеством или сортировать.

export interface KvBackend {
  get(key: string): Promise<unknown | null>;
  set(key: string, value: unknown): Promise<void>;
  /** Ключи, начинающиеся с префикса. Без префикса — все. */
  keys(prefix?: string): Promise<string[]>;
  del(keys: string[]): Promise<void>;
}

export function memoryBackend(seed?: Map<string, unknown>): KvBackend {
  const m = seed ?? new Map<string, unknown>();
  return {
    async get(key) { return m.has(key) ? m.get(key)! : null; },
    async set(key, value) { m.set(key, value); },
    async keys(prefix) {
      const all = Array.from(m.keys());
      return prefix ? all.filter((k) => k.startsWith(prefix)) : all;
    },
    async del(keys) { for (const k of keys) m.delete(k); },
  };
}
